/**
 * Processing Indicator Component
 *
 * Global indicator that shows when OCR processing is active.
 * Persists across navigation and provides user feedback.
 */

import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Icon } from '../ui/Icon';
import type { IconName } from '../ui/Icon';
import {
  useTaskProcessingStore,
  selectPendingProcessingTasks,
} from '../../stores/taskProcessing.store';
import type { ProcessingTask } from '../../stores/taskProcessing.types';
import type { NormalizedTaskStatus } from '../../lib/schemas/task.schema';
import { formatRelativeTime } from '../../utils/formatters';

// ============================================================================
// Animations
// ============================================================================

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const slideIn = keyframes`
  from { 
    transform: translateY(-100%);
    opacity: 0;
  }
  to { 
    transform: translateY(0);
    opacity: 1;
  }
`;

// ============================================================================
// Styled Components
// ============================================================================

const IndicatorWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const IndicatorContainer = styled.button<{ $isVisible: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  background: var(--brand-50);
  border: 1px solid var(--brand-200);
  border-radius: var(--radius-md);
  color: var(--brand-700);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  box-shadow: none;
  transition: all 0.3s ease;

  ${(props) =>
    props.$isVisible
      ? css`
          animation: ${slideIn} 0.3s ease-out;
          opacity: 1;
          transform: translateY(0);
        `
      : css`
          opacity: 0;
          transform: translateY(-100%);
          pointer-events: none;
        `}

  /* Responsive adjustments */
  @media (max-width: 768px) {
    font-size: var(--font-size-xs);
    padding: var(--space-xs) var(--space-sm);
    gap: var(--space-xs);
  }
`;

const SpinningIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${spin} 1.5s linear infinite;
  color: var(--brand-600);
`;

const ProcessingText = styled.span`
  cursor: pointer;
  white-space: nowrap;

  @media (max-width: 480px) {
    display: none; /* Hide text on very small screens */
  }
`;

const Counter = styled.span`
  background: var(--brand-100);
  color: var(--brand-800);
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  min-width: 20px;
  text-align: center;
`;

const TooltipPanel = styled.div`
  position: absolute;
  top: calc(100% + var(--space-xs));
  right: 0;
  z-index: var(--z-tooltip);
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  width: min(320px, 80vw);
  padding: var(--space-md);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
`;

const TooltipList = styled.ul`
  display: grid;
  gap: var(--space-sm);
  margin: 0;
  padding: 0;
  list-style: none;
`;

const TooltipListItem = styled.li`
  display: flex;
  gap: var(--space-sm);
`;

const TooltipItemIcon = styled.span<{ $status: NormalizedTaskStatus }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: var(--radius-full);
  background: ${({ $status }) =>
    $status === 'failure'
      ? 'var(--danger-50)'
      : $status === 'retrying'
        ? 'var(--warning-50)'
        : $status === 'success'
          ? 'var(--success-50)'
          : $status === 'queued'
            ? 'var(--info-50)'
            : $status === 'canceled'
              ? 'var(--neutral-100)'
              : 'var(--brand-50)'};
  color: ${({ $status }) =>
    $status === 'failure'
      ? 'var(--danger-600)'
      : $status === 'retrying'
        ? 'var(--warning-600)'
        : $status === 'success'
          ? 'var(--success-600)'
          : $status === 'queued'
            ? 'var(--info-600)'
            : $status === 'canceled'
              ? 'var(--neutral-600)'
              : 'var(--brand-600)'};
`;

const TooltipItemMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TooltipItemTitle = styled.span`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
`;

const TooltipItemSubtitle = styled.span`
  font-size: var(--font-size-xs);
  color: var(--text-muted);
`;

const TooltipEmpty = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: var(--font-size-sm);
  color: var(--text-muted);
`;

const TooltipFooter = styled.div`
  margin-top: var(--space-sm);
  font-size: var(--font-size-xs);
  color: var(--text-muted);
`;

const TooltipHint = styled.div`
  margin-top: var(--space-xs);
  font-size: var(--font-size-xs);
  color: var(--text-muted);
`;

// ============================================================================
// Component
// ============================================================================

interface ProcessingIndicatorProps {
  className?: string;
  showCounter?: boolean;
  compactMode?: boolean;
}

export const ProcessingIndicator: React.FC<ProcessingIndicatorProps> = ({
  className,
  showCounter = true,
  compactMode = false,
}) => {
  const pendingTasks = useTaskProcessingStore(selectPendingProcessingTasks);
  const activeCount = pendingTasks.length;
  const tooltipTasks = React.useMemo(
    () => pendingTasks.slice(0, 5),
    [pendingTasks],
  );
  const remainingCount = React.useMemo(
    () => Math.max(activeCount - tooltipTasks.length, 0),
    [activeCount, tooltipTasks.length],
  );
  const tooltipId = React.useId();
  const [isTooltipVisible, setTooltipVisible] = React.useState(false);

  const openTooltip = React.useCallback(() => {
    setTooltipVisible(true);
  }, []);

  const closeTooltip = React.useCallback(() => {
    setTooltipVisible(false);
  }, []);

  const handleBlur = React.useCallback(
    (event: React.FocusEvent<HTMLButtonElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        closeTooltip();
      }
    },
    [closeTooltip],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Escape') {
        closeTooltip();
      }
    },
    [closeTooltip],
  );

  if (activeCount === 0) {
    return null;
  }

  const getProcessingText = () => {
    if (compactMode) {
      return 'Procesando...';
    }

    return `Procesando ${activeCount} archivo${activeCount > 1 ? 's' : ''}`;
  };

  const getTaskDisplayName = (task: ProcessingTask): string => {
    const filename =
      typeof task.details?.filename === 'string' ? task.details.filename : null;
    if (filename && filename.length > 0) {
      return filename;
    }

    if (task.subtype && task.subtype !== 'unknown') {
      return task.subtype;
    }

    return 'Tarea en curso';
  };

  const getTaskTimestampLabel = (task: ProcessingTask): string => {
    const timestamp =
      task.timestamps.completedAt ??
      task.timestamps.startedAt ??
      task.timestamps.queuedAt;

    if (!timestamp) {
      return 'Sin detalles de tiempo';
    }

    return formatRelativeTime(timestamp);
  };

  return (
    <IndicatorWrapper className={className} onMouseLeave={closeTooltip}>
      <IndicatorContainer
        $isVisible={true}
        type="button"
        onClick={openTooltip}
        aria-label={`Procesando ${activeCount} archivo${activeCount > 1 ? 's' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={isTooltipVisible}
        aria-controls={isTooltipVisible ? tooltipId : undefined}
        tabIndex={0}
        onMouseEnter={openTooltip}
        onFocus={openTooltip}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      >
        <SpinningIcon>
          <Icon name="loading" size="sm" aria-hidden={true} />
        </SpinningIcon>

        <ProcessingText role="status" aria-live="polite">
          {getProcessingText()}
        </ProcessingText>

        {showCounter && activeCount > 1 && (
          <Counter aria-label={`${activeCount} archivos`}>
            {activeCount}
          </Counter>
        )}

        {/* Screen reader announcement */}
        <span className="sr-only">
          {`Procesando ${activeCount} archivo${activeCount > 1 ? 's' : ''} en segundo plano. Puedes navegar por la aplicación normalmente.`}
        </span>
      </IndicatorContainer>

      {isTooltipVisible && (
        <TooltipPanel
          id={tooltipId}
          role="dialog"
          aria-label="Tareas en progreso"
        >
          {tooltipTasks.length > 0 ? (
            <TooltipList>
              {tooltipTasks.map((task) => {
                if (!task) {
                  return null;
                }

                const status = task.normalizedStatus;
                const icon = STATUS_ICON_MAP[status];
                const statusLabel = STATUS_LABEL_MAP[status];
                const title = getTaskDisplayName(task);
                const timeLabel = getTaskTimestampLabel(task);

                return (
                  <TooltipListItem key={task.id}>
                    <TooltipItemIcon $status={status}>
                      <Icon name={icon} size="sm" aria-hidden />
                    </TooltipItemIcon>
                    <TooltipItemMeta>
                      <TooltipItemTitle title={title}>{title}</TooltipItemTitle>
                      <TooltipItemSubtitle>
                        {statusLabel} • {timeLabel}
                      </TooltipItemSubtitle>
                    </TooltipItemMeta>
                  </TooltipListItem>
                );
              })}
            </TooltipList>
          ) : (
            <TooltipEmpty>
              <Icon name="info" size="sm" aria-hidden />
              Sin tareas en progreso.
            </TooltipEmpty>
          )}

          {remainingCount > 0 && (
            <TooltipFooter>{`+ ${remainingCount} tarea${remainingCount > 1 ? 's' : ''} adicional${remainingCount > 1 ? 'es' : ''}`}</TooltipFooter>
          )}

          <TooltipHint>
            Mantén el cursor o el foco para ver los detalles.
          </TooltipHint>
        </TooltipPanel>
      )}
    </IndicatorWrapper>
  );
};

/**
 * Compact version for use in smaller spaces (e.g., mobile navigation)
 */
export const CompactProcessingIndicator: React.FC<{ className?: string }> = ({
  className,
}) => {
  return (
    <ProcessingIndicator
      {...(className ? { className } : {})}
      showCounter={false}
      compactMode={true}
    />
  );
};

/**
 * Header version with specific styling for header integration
 */
export const HeaderProcessingIndicator: React.FC = () => {
  return <ProcessingIndicator showCounter={true} compactMode={false} />;
};

ProcessingIndicator.displayName = 'ProcessingIndicator';
CompactProcessingIndicator.displayName = 'CompactProcessingIndicator';
HeaderProcessingIndicator.displayName = 'HeaderProcessingIndicator';

const STATUS_ICON_MAP: Record<NormalizedTaskStatus, IconName> = {
  queued: 'time',
  processing: 'refresh',
  retrying: 'warning',
  success: 'success',
  failure: 'error',
  canceled: 'info',
};

const STATUS_LABEL_MAP: Record<NormalizedTaskStatus, string> = {
  queued: 'En cola',
  processing: 'Procesando',
  retrying: 'Reintentando',
  success: 'Completado',
  failure: 'Error',
  canceled: 'Cancelado',
};
